// pages/FeatureDetailPage.jsx - Detail page for each feature with assets
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './FeatureDetailPage.css';

// Import logos
import mainLogo from './assets/logos/logo.png';
import advofinder from './assets/logos/advofinder.png';
import advoconnect from './assets/logos/advoconnect.png';
import advoplanner from './assets/logos/advoplanner.png';
import aiAlly from './assets/logos/aially.png';
import advointern from './assets/logos/advointern.png';

// Import social media icons
import linkedinIcon from './assets/logos/linkedin.png';
import twitterIcon from './assets/logos/twitter.png';
import instagramIcon from './assets/logos/instagram.png';

// Home icon
import homeIcon from './assets/logos/home.png';

const FeatureDetailPage = () => {
  const { featureId } = useParams();
  const navigate = useNavigate();

  const features = {
    advofinder: {
      name: 'AdvoFinder',
      logo: advofinder,
      description: 'Discover verified law firms instantly without rankings, ads, or promotions.',
      keyFeatures: [
        'Filter by city, case type, language, and locality',
        'Real-time “Available Now" status for firms',
        'Request-based contact forms',
        'Verified firm profiles only - no individual advocate listings'
      ],
      keyComponents: [
        'Search filters for practice area, city, and specialization',
        'Status tracker for firm availability'
      ]
    },
    advoconnect: {
      name: 'AdvoConnect',
      logo: advoconnect,
      description: 'A space for verified legal professionals and students to connect and share knowiedge',
      keyFeatures: [
        'Verified user profiles',
        'Mentorship space',
        'Public legal discussions',
        'Role-based tagging'
      ],
      keyComponents: [
        'Topic categorization',
        'Profile verification',
        'Content moderation tools',
        'Read-only public access'
      ]
    },
    advoplanner: {
      name: 'AdvoPlanner',
      logo: advoplanner,
      description: 'Smart legal productivity tool for students and advocates - organize deadlines, goals, and milestones privately.',
      keyFeatures: [
        'Monthly, weekly, and daily task views',
        'Smart legal to-do integration',
        'Document uploads for personal tracking',
        'Custom reminder and alert system'
      ],
      keyComponents: [
        'Moot & internship calendar sync',
        'Exam & court date tracker',
        'Private reading/resource logs'
      ]
    },
    aiAlly: {
      name: 'AiAlly',
      logo: aiAlly,
      description: 'Your Al-powered legal research assistant - fast, interactive, and reliable.',
      keyFeatures: [
        'Conversational interface for legal queries',
        'Quick Al-generated drafting templates',
        'Context memory for follow-up questions',
        'Legal citation previews for awareness'
      ],
      keyComponents: [
        'Voice and text input options',
        'Document formatting & structure assistance',
        'Definitions of legal terms and procedures',
        'Escalation path to verified legal educators'
      ]
    },
    advointern: {
      name: 'AdvoIntern',
      logo: advointern,
      description: 'A verified internship platform for lawstudents to explore, apply, and grow - fair, secure, and bias-free.',
      keyFeatures: [
        'Structured internship listings',
        'Quick-apply application flow',
        'Transparent tracking dachboard',
        'Skill-tagged profile match'
      ],
      keyComponents: [
        'Resume builder tools',
        'Legal skill assessments',
        'Interview scheduling system',
        'Internship milestone tracker'
      ]
    }
  };

  const feature = features[featureId] || features.advofinder;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  const handleFeatureChange = (newFeatureId) => {
    navigate(`/feature/${newFeatureId}`);
  };

  const goToHome = () => {
    navigate('/');
  };

  return (
    <div className={`feature-detail-container ${featureId}-bg show-content`}>
      <div className="detail-header">
        <button className="home-button" onClick={goToHome}>
          <img src={homeIcon} alt="Home" className="home-icon-image" />
        </button>
      </div>

      <div className="logo-container">
        <div className="feature-logo">
          <img 
            src={feature.logo} 
            alt={`${feature.name} Logo`} 
            className="feature-logo-image" 
          />
        </div>
      </div>

      <div className="detail-intro">
        <h1 className="detail-title">{feature.name}</h1>
        <p className="detail-description">{feature.description}</p>
        <p className="detail-tagline">{feature.tagline}</p>
      </div>

      <div className="detail-divider"></div>

      <div className="detail-section">
        <h2 className="section-title">Key Features</h2>
        <ul className="detail-list">
          {feature.keyFeatures.map((item, index) => (
            <li key={`feature-${index}`} className="detail-list-item">
              <span className="list-bullet">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="detail-section">
        <h2 className="section-title">Key Components</h2>
        <ul className="detail-list">
          {feature.keyComponents.map((item, index) => (
            <li key={`component-${index}`} className="detail-list-item">
              <span className="list-bullet">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="detail-divider"></div>

      <div className="detail-section">
        <h2 className="section-title">Know more about-</h2>
        <div className="related-features">
          {Object.entries(features)
            .filter(([id]) => id !== featureId)
            .map(([id, otherFeature]) => (
              <button 
                key={id} 
                className="related-feature-item" 
                onClick={() => handleFeatureChange(id)}
              >
                <div className="related-feature-icon">
                  <img 
                    src={otherFeature.logo} 
                    alt={`${otherFeature.name} Logo`} 
                    className="related-feature-icon-image" 
                  />
                </div>
                <span className="related-feature-name">{otherFeature.name}</span>
              </button>
            ))}
        </div>
      </div>

      <div className="detail-divider"></div>

      <div className="detail-footer">
        <div className="footer-links">
          <div className="social-links">
            <a href="https://www.linkedin.com/company/advoai" className="social-icon">
              <img src={linkedinIcon} alt="LinkedIn" className="social-icon-image" />
            </a>
            <a href="https://www.x.com/advo_ai" className="social-icon">
              <img src={twitterIcon} alt="Twitter" className="social-icon-image" />
            </a>
            <a href="https://www.instagram.com/advoai/" className="social-icon">
              <img src={instagramIcon} alt="Instagram" className="social-icon-image" />
            </a>
          </div>
        </div>
      </div>

      <div className="detail-divider"></div>

      <div className="app-promotion">
        <p className="promotion-tagline">Do more with AdvoAi</p>
        <h3 className="promotion-title">One app for<br />all things Legal</h3>

        <div className="promotion-logo-container">
          <img src={mainLogo} alt="AdvoAI Logo" className="promotion-logo-image" />
        </div>

        <div className="promotion-company">
          <p className="promotion-company-name">AdvoAI Technologies<br />Pvt. Ltd.</p>
          <p className="promotion-made-in">
            Proudly made in India 🇮🇳<br />
            100% Safe and Secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetailPage;